import { Component, createRef } from "react"

/* Livekit */
import * as LiveKit from "livekit-client"

/* REST API */
import { create } from "../api/Stand"

/* Helpers */
import withRouter from "../helpers/withRouter"


/* HOW LONG THE STAND WAITS BEFORE IT TRIES TO CONNECT AGAIN */
const RETRY = 5000


/* Widget Stand */
class Stand extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            started: false,
            muted: false,
        }

        this._video = createRef()
        this._screen = createRef()

        this._room = null
        this._timer = null
        this._wake = null
        this._mounted = false
    }

    componentDidMount = () => {

        this._mounted = true

        /* The first play is always muted, the browser blocks the sound before the touch of the user */
        if (this._video.current) {
            this._video.current.muted = true
        }

        /* The remote of the TV sends the Enter, so the start works without the mouse */
        window.addEventListener("keydown", this.key)

        /* The screen lock is taken again when the TV comes back from the sleep */
        document.addEventListener("visibilitychange", this.visibility)

        this.load()
    }

    componentWillUnmount = () => {

        this._mounted = false

        window.removeEventListener("keydown", this.key)
        document.removeEventListener("visibilitychange", this.visibility)

        this.clear()

        /* Release Screen */
        if (this._wake) {
            this._wake.release().catch(() => { })
            this._wake = null
        }
    }

    /* Read the parts of the link /:key?studio=&room= */
    link = () => {

        /* Fields */
        const { params, search } = this.props

        return {
            key: params.key,
            studio: search.get("studio"),
            room: search.get("room"),
        }
    }

    /* Clear the room and the timer */
    clear = () => {

        /* Check Timer */
        if (this._timer) {
            clearTimeout(this._timer)
            this._timer = null
        }

        /* Check Room — the events are dropped first, so the own disconnect does not start the retry */
        if (this._room) {
            this._room.removeAllListeners()
            this._room.disconnect()
            this._room = null
        }

        /* Check Video */
        if (this._video.current) {
            this._video.current.srcObject = null
        }
    }

    /* Try to connect again */
    retry = () => {

        /* Check Mount */
        if (!this._mounted || this._timer) {
            return
        }

        this._timer = setTimeout(() => {
            this._timer = null
            this.load()
        }, RETRY)
    }

    /* Load Action — the token of the room is taken by the static key of the link */
    load = () => {

        /* Fields */
        const { key, studio, room } = this.link()

        /* Check Link */
        if (!key || !studio || !room) {
            this.setState({ status: "link" })
            return
        }

        /* Send to REST API */
        create(key, { studio, room }).then((response) => {

            /* Check Mount */
            if (!this._mounted) {
                return
            }

            /* Check Response — the empty response means the network is down */
            if (!response) {
                this.setState({ status: "network" })
                this.retry()
                return
            }

            if (response.status === 200) {
                this.connect(response.data.socketURL, response.data.token)
                return
            }

            if (response.status === 401) {
                this.setState({ status: "permission" })
                return
            }

            if (response.status === 404) {
                this.setState({ status: "studio" })
                return
            }

            this.setState({ status: "error" })
            this.retry()

        })
    }

    /* Connect to the room of the studio */
    connect = async (socketURL, token) => {

        /* Check Response */
        if (!socketURL || !token) {
            this.setState({ status: "error" })
            this.retry()
            return
        }

        /* Drop the previous room before the new one is created */
        this.clear()

        /* Create Room */
        const room = new LiveKit.Room({ adaptiveStream: true, dynacast: true })
        this._room = room

        /* On Connect */
        room.on(LiveKit.RoomEvent.TrackSubscribed, (track) => {

            /* Get Video */
            const video = this._video.current

            /* Check Video */
            if (!video) {
                return
            }

            video.autoplay = true
            video.playsInline = true

            /* Add Video */
            if (track.kind === "video") {
                track.attach(video)
            }

            /* Add Audio */
            if (track.kind === "audio") {
                track.attach(video)
            }

            /* The sound is on only after the start, before it the browser blocks the play */
            video.muted = !this.state.started
            video.volume = this.state.started ? 1 : 0

            video.play().catch(() => { })

            this.setState({ status: "live" })
        })

        /* On Disconnect */
        room.on(LiveKit.RoomEvent.Disconnected, () => {

            /* Check Mount */
            if (!this._mounted) {
                return
            }

            this.setState({ status: "reconnect" })
            this.retry()
        })

        try {

            /* Connect */
            await room.connect(socketURL, token)

            /* Set Parameters — the stand never sends the camera and the microphone */
            room.localParticipant.setMicrophoneEnabled(false)
            room.localParticipant.setCameraEnabled(false)

            /* Check Mount — the track can come before the connect ends, so the live is never dropped */
            if (this._mounted) {
                this.setState((state) => state.status === "live" ? null : { status: "wait" })
            }
        }
        catch {

            /* Check Mount */
            if (this._mounted) {
                this.setState({ status: "error" })
                this.retry()
            }
        }
    }

    /* Keep the screen of the TV awake */
    wake = async () => {

        try {

            /* Check Support */
            if (!("wakeLock" in navigator) || this._wake) {
                return
            }

            this._wake = await navigator.wakeLock.request("screen")

            this._wake.addEventListener("release", () => {
                this._wake = null
            })
        }
        catch { }
    }

    /* Visibility Action */
    visibility = () => {

        /* Check State */
        if (document.visibilityState === "visible" && this.state.started) {
            this.wake()
        }
    }

    /* Key Action — the remote of the TV gives the Enter, the arrows and the letter M */
    key = (event) => {

        /* Fields */
        const { started } = this.state

        /* The first Enter starts the stand, the next ones take the full screen back */
        if (event.key === "Enter" || event.key === " ") {

            /* Check State */
            if (!started) {
                this.start()
                return
            }

            this.screen()
            return
        }

        /* The M turns the sound off and on — the second letter is the same key of the russian layout */
        if (event.key === "m" || event.key === "M" || event.key === "ь" || event.key === "Ь") {
            this.mute()
            return
        }

        /* The arrows change the volume */
        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            this.volume(event.key === "ArrowUp" ? 0.1 : -0.1)
        }
    }


    /* Full Screen Action — the same key takes the full screen and gives it back */
    screen = () => {

        /* Get Element — the browser keeps it only while the full screen is on */
        const element = document.fullscreenElement || document.webkitFullscreenElement

        /* Check Element */
        if (element) {

            /* Get Exit */
            const exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen

            /* Check Exit */
            if (exit) {
                try {
                    const result = exit.call(document)
                    if (result && result.catch) {
                        result.catch(() => { })
                    }
                }
                catch { }
            }

            return
        }

        /* Get Screen */
        const screen = this._screen.current

        /* Check Screen */
        if (!screen) {
            return
        }

        /* Get Request */
        const request = screen.requestFullscreen || screen.webkitRequestFullscreen || screen.webkitRequestFullScreen || screen.msRequestFullscreen

        /* Check Request */
        if (request) {
            try {
                const result = request.call(screen, { navigationUI: "hide" })
                if (result && result.catch) {
                    result.catch(() => { })
                }
            }
            catch { }
        }
    }


    /* Mute Action — the sound goes off without the exit from the full screen */
    mute = () => {

        /* Get Video */
        const video = this._video.current

        /* Check Video — the sound lives only after the start */
        if (!video || !this.state.started) {
            return
        }

        video.muted = !video.muted

        /* The volume comes back when the sound is turned on again */
        if (!video.muted && video.volume === 0) {
            video.volume = 1
        }

        this.setState({ muted: video.muted })
    }


    /* Volume Action */
    volume = (step) => {

        /* Get Video */
        const video = this._video.current

        /* Check Video */
        if (!video || !this.state.started) {
            return
        }

        /* The volume stays between the zero and the one */
        video.volume = Math.min(1, Math.max(0, Math.round((video.volume + step) * 10) / 10))

        /* The sound comes back when the volume is raised */
        if (video.volume > 0) {
            video.muted = false
        }

        this.setState({ muted: video.muted || video.volume === 0 })
    }

    /* Start Action — the full screen and the sound are allowed only inside the click of the user */
    start = () => {

        /* Fields */
        const { started } = this.state

        /* Check State */
        if (started) {
            return
        }

        /* Full Screen */
        this.screen()

        /* Get Video */
        const video = this._video.current

        /* Sound */
        if (video) {
            video.muted = false
            video.volume = 1
            video.play().catch(() => { })
        }

        this.setState({ started: true, muted: false })

        this.wake()
    }

    /* Draw the text of the state */
    _text = () => {

        /* Fields */
        const { status } = this.state

        /* Check Status */
        switch (status) {
            case "link": return "Неверная ссылка"
            case "permission": return "Неверный ключ"
            case "studio": return "Студия не найдена"
            case "network": return "Нет соединения"
            case "reconnect": return "Переподключение"
            case "error": return "Ошибка соединения"
            default: return "Подключение"
        }
    }

    /* Draw the cover with the play button */
    _cover = () => {

        /* Fields */
        const { status, started } = this.state

        /* Check State */
        if (started) {
            return null
        }

        /* Fields */
        const { room } = this.link()

        /* The stream is ready, the stand waits for the touch of the user */
        const ready = status === "live" || status === "wait"

        return (
            <div className="stand--cover">

                {ready
                    ? <button onClick={() => this.start()} className="stand--play" type="button" autoFocus>
                        <svg viewBox="0 0 24 24" width="64" height="64" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                    </button>
                    : <div className={`stand--state ${status === "link" || status === "permission" || status === "studio" ? "bad" : ""}`}>
                        <i />
                        <span>{this._text()}</span>
                    </div>
                }

                {ready && <div className="stand--room">{room}</div>}

                {/* The TV has no mouse, so the keys of the remote are written down */}
                {ready && <div className="stand--hint">OK — во весь экран · M — звук · ↑ ↓ — громкость</div>}

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status, started, muted } = this.state

        return (
            <div ref={this._screen} className={`stand ${started ? "started" : ""}`}>

                <video ref={this._video} className="stand--video" autoPlay playsInline />

                {/* Draw Cover */}
                {this._cover()}

                {/* The badge shows that the stream is alive */}
                {started && status === "live" && <div className="stand--live"><i />LIVE</div>}

                {/* The stream was lost after the start */}
                {started && status !== "live" && <div className="stand--lost"><i />{this._text()}</div>}

                {/* The sound is off, the badge tells how it comes back */}
                {started && muted && <div className="stand--mute">MUTE — M</div>}

            </div>
        )
    }
}

export default withRouter(Stand)
